import React from 'react';

/**
 * ObservabilityClip
 * Looping SVG clip showing a live observability dashboard:
 *  - Streaming KPI line chart
 *  - Pulsing telemetry sources feeding in
 *  - Scanning beam + animated metric tiles
 */
const ObservabilityClip = () => {
  return (
    <div className="relative w-full" data-testid="observability-clip">
      <style>{`
        @keyframes obs-stream {
          0%   { stroke-dashoffset: 600; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes obs-pulse {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.18); }
        }
        @keyframes obs-tile {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
        @keyframes obs-cursor {
          0%   { transform: translateX(0); }
          100% { transform: translateX(360px); }
        }
        @keyframes obs-bar {
          0%, 100% { height: 14px; }
          50%      { height: 30px; }
        }
        @keyframes obs-scan {
          0%   { transform: translateY(-100%); opacity: 0; }
          15%  { opacity: 0.55; }
          85%  { opacity: 0.55; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        .obs-line   { animation: obs-stream 5s linear infinite; }
        .obs-cursor { animation: obs-cursor 5s linear infinite; transform-origin: center; }
        .obs-dot    { animation: obs-pulse 1.8s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
        .obs-tile   { animation: obs-tile 2.4s ease-in-out infinite; }
        .obs-bar    { animation: obs-bar 1.8s ease-in-out infinite; transform-origin: bottom; }
        .obs-scan   { animation: obs-scan 5s ease-in-out infinite; }
      `}</style>

      <div className="relative rounded-2xl border border-neutral-800 bg-gradient-to-br from-neutral-950 via-black to-neutral-950 p-5 overflow-hidden">
        <div className="flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-2 text-neutral-400">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400" style={{ animation: 'obs-pulse 1.8s ease-in-out infinite' }} />
            <span className="uppercase tracking-[0.18em]">Live telemetry · observability</span>
          </div>
          <div className="flex gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
          </div>
        </div>

        <div aria-hidden className="absolute inset-0 opacity-[0.18] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse at center, black 50%, transparent 90%)',
        }} />
        <div aria-hidden className="absolute inset-x-0 top-0 h-full pointer-events-none">
          <div className="obs-scan absolute inset-x-0 h-24" style={{
            background: 'linear-gradient(to bottom, transparent, rgba(59,130,246,0.18), transparent)',
            filter: 'blur(6px)',
          }} />
        </div>

        {/* KPI tiles */}
        <div className="relative mt-3 grid grid-cols-3 gap-2">
          {[
            { k: 'Throughput',    v: '12.4k/s', d: '+3.2%' },
            { k: 'p95 latency',   v: '184 ms',  d: '−12 ms' },
            { k: 'Open anomalies',v: '3',       d: 'auto-resolving' },
          ].map((t, i) => (
            <div key={t.k} className="obs-tile rounded-lg border border-neutral-800 bg-neutral-950 px-3 py-2" style={{ animationDelay: `${i * 0.3}s` }}>
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-neutral-500">{t.k}</div>
              <div className="text-[14px] font-semibold text-white tabular-nums leading-tight">{t.v}</div>
              <div className="text-[9.5px] text-blue-400">{t.d}</div>
            </div>
          ))}
        </div>

        {/* Stream chart */}
        <svg viewBox="0 0 420 160" className="relative w-full h-auto mt-3" role="img" aria-label="Observability stream chart">
          <defs>
            <linearGradient id="obs-fill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%"  stopColor="#3b82f6" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="obs-stroke" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#60a5fa" />
              <stop offset="100%" stopColor="#22d3ee" />
            </linearGradient>
          </defs>

          {/* axes */}
          <line x1="20" y1="120" x2="400" y2="120" stroke="#1f2937" strokeWidth="1" />
          <line x1="20" y1="20"  x2="20"  y2="120" stroke="#1f2937" strokeWidth="1" />
          {[40, 60, 80, 100].map((y) => (
            <line key={y} x1="20" y1={y} x2="400" y2={y} stroke="#111827" strokeDasharray="2 4" />
          ))}

          {/* area fill */}
          <path d="M 20 90 L 60 80 L 100 95 L 140 70 L 180 85 L 220 55 L 260 75 L 300 50 L 340 68 L 380 45 L 400 60 L 400 120 L 20 120 Z" fill="url(#obs-fill)" />

          {/* primary stream line */}
          <path
            className="obs-line"
            d="M 20 90 L 60 80 L 100 95 L 140 70 L 180 85 L 220 55 L 260 75 L 300 50 L 340 68 L 380 45 L 400 60"
            fill="none"
            stroke="url(#obs-stroke)"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeDasharray="600"
          />

          {/* cursor */}
          <g className="obs-cursor">
            <line x1="20" y1="20" x2="20" y2="120" stroke="#3b82f6" strokeOpacity="0.55" strokeWidth="1" strokeDasharray="2 3" />
            <circle cx="20" cy="60" r="3.5" fill="#60a5fa" />
            <circle cx="20" cy="60" r="3.5" fill="none" stroke="#60a5fa" className="obs-dot" />
          </g>

          {/* telemetry sources (left) */}
          {[
            { y: 36,  label: 'ERP' },
            { y: 78,  label: 'CRM' },
            { y: 110, label: 'ITSM' },
          ].map((s) => (
            <g key={s.label}>
              <circle cx="6" cy={s.y} r="2.5" fill="#3b82f6" className="obs-dot" />
              <line x1="9" y1={s.y} x2="18" y2={s.y} stroke="#1e3a8a" strokeWidth="1" />
            </g>
          ))}

          {/* mini bars (right) */}
          {[300, 320, 340, 360, 380].map((x, i) => (
            <rect
              key={x}
              className="obs-bar"
              x={x}
              y={120}
              width="6"
              height="14"
              fill="#1d4ed8"
              opacity="0.55"
              style={{ animationDelay: `${i * 0.15}s`, transformOrigin: `${x + 3}px 120px` }}
            />
          ))}
        </svg>

        <div className="mt-1 flex items-center justify-between text-[10.5px] text-neutral-500">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Throughput</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /> Latency</span>
          </div>
          <span className="tabular-nums">sample · 1.0s</span>
        </div>
      </div>
    </div>
  );
};

export default ObservabilityClip;
